import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Category } from './types';
import { categoryService } from './services/categoryService';
import './CategoryDropdown.css';

interface CategoryDropdownProps {
  parentType: string; // 'contact', 'phone', 'email', 'address', 'property', etc.
  value: number | string; // Allow both number (ID) and string for backward compatibility
  onChange: (value: number) => void; // Always return number (ID)
  className?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  /** When provided, refetch categories when this value changes (e.g. after adding a category) */
  refreshKey?: string | number;
}

function isTaxCategory(cat: Category): boolean {
  return Number(cat.tax) === 1;
}

const CategoryDropdown: React.FC<CategoryDropdownProps> = ({
  parentType,
  value,
  onChange,
  className = '',
  placeholder = 'Select...',
  required = false,
  disabled = false,
  refreshKey,
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const useExpenseRichPicker = parentType === 'expense';

  const fetchCategories = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await categoryService.getCategoriesByType(parentType);
      setCategories(data.filter((cat) => Boolean(cat.isActive)));
    } catch (err) {
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to fetch categories');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [parentType]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories, refreshKey]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchCategories(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchCategories]);

  useEffect(() => {
    if (!panelOpen || !useExpenseRichPicker) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setPanelOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [panelOpen, useExpenseRichPicker]);

  useEffect(() => {
    if (!panelOpen || !useExpenseRichPicker) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPanelOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [panelOpen, useExpenseRichPicker]);

  useEffect(() => {
    setPanelOpen(false);
  }, [value, disabled]);

  if (loading) {
    return (
      <select className={`app-form-select ${className}`.trim()} disabled>
        <option>Loading...</option>
      </select>
    );
  }

  if (error) {
    return (
      <select className={`app-form-select ${className}`.trim()} disabled>
        <option>Error loading categories</option>
      </select>
    );
  }

  const numericValue = value === '' || value === null || value === undefined ? 0 : Number(value);
  const selectedCategory = numericValue ? categories.find((c) => c.id === numericValue) : undefined;

  if (useExpenseRichPicker) {
    const showClearOption = numericValue === 0 || !required;

    return (
      <div
        ref={containerRef}
        className={`category-dropdown-expense ${className}`.trim()}
      >
        <button
          type="button"
          className={`app-form-select category-dropdown-expense-trigger`.trim()}
          disabled={disabled}
          aria-expanded={panelOpen}
          aria-haspopup="listbox"
          onClick={() => {
            if (disabled) return;
            setPanelOpen((open) => !open);
          }}
          onFocus={() => fetchCategories(true)}
        >
          <span className="category-dropdown-expense-trigger-inner">
            {selectedCategory ? (
              <>
                <span>{selectedCategory.name}</span>
                {isTaxCategory(selectedCategory) && (
                  <span className="category-dropdown-tax-badge">(tax)</span>
                )}
              </>
            ) : (
              <span className="category-dropdown-placeholder">{placeholder}</span>
            )}
          </span>
          <span className="category-dropdown-chevron" aria-hidden="true">
            ▾
          </span>
        </button>

        {panelOpen && (
          <ul className="category-dropdown-expense-panel" role="listbox">
            {showClearOption && (
              <li role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={numericValue === 0}
                  onClick={() => {
                    onChange(0);
                  }}
                >
                  <span className="category-dropdown-expense-option-label">{placeholder}</span>
                </button>
              </li>
            )}
            {categories.map((category) => (
              <li key={category.id} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={category.id === numericValue}
                  onClick={() => {
                    onChange(category.id);
                  }}
                >
                  <span className="category-dropdown-expense-option-label">{category.name}</span>
                  {isTaxCategory(category) && (
                    <span className="category-dropdown-tax-badge">(tax)</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <select
      className={`app-form-select ${className}`.trim()}
      value={numericValue === 0 ? '' : numericValue}
      onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
      onFocus={() => fetchCategories(true)}
      required={required}
      disabled={disabled}
    >
      {(numericValue === 0 || !required) && <option value="">{placeholder}</option>}
      {categories.map((category) => (
        <option key={category.id} value={category.id}>
          {category.name}
        </option>
      ))}
    </select>
  );
};

export default CategoryDropdown;
